import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import {
  savePageDataById,
  updateProductInStockNotify
} from 'store/reducers/productsSlice';

import {
  BrandType,
  CategoryType,
  FavoriteToggleResponse,
  FilterSubType,
  GetProductsByFilterParams,
  ProductItem,
  ProductTypeListParams,
  SuccessResponse
} from 'types';

/* eslint import/no-cycle: [2, { maxDepth: 1 }] */
import api from '../api/products';
import { getErrorMessage, serializeQueryError } from '../utils/rtkQueryError';

export const productsApi = createApi({
  reducerPath: 'productsApi',
  baseQuery: fetchBaseQuery({ baseUrl: '/' }),
  tagTypes: ['ProductsByFilter', 'ProductDetail'],
  endpoints: (builder) => ({
    getProductsByType: builder.query<ProductItem[], ProductTypeListParams>({
      queryFn: async (type) => {
        try {
          const { data } = await api.getProductsByType({
            [type]: 1
          });

          return { data: data?.items ?? [] };
        } catch (error) {
          console.error(
            `Error while get products list: ${getErrorMessage(error)}`
          );

          return { error: serializeQueryError(error) };
        }
      }
    }),
    getPurchasedProducts: builder.query<ProductItem[], void>({
      queryFn: async () => {
        try {
          const { data } = await api.getPurchasedProducts();

          return { data: data || [] };
        } catch (error) {
          console.error(
            `Error while getting purchased products: ${error.message}`
          );

          return { error: serializeQueryError(error) };
        }
      }
    }),
    toggleFavoriteProduct: builder.mutation({
      queryFn: async (data: {
        product: ProductItem;
        isRemoveAction: boolean;
      }) => {
        const { product, isRemoveAction = false } = data;
        try {
          let response: FavoriteToggleResponse = {};
          if (isRemoveAction) {
            const { data: deleteResponse } =
              await api.deleteProductFromFavorite(product.id);
            response = deleteResponse;
          } else {
            const { data: addResponse } = await api.addProductToFavorite(
              product.id
            );
            response = addResponse;
          }

          return { data: response };
        } catch (error) {
          console.error(`Error while add favorite location:: ${error.message}`);

          return { error: serializeQueryError(error) };
        }
      },
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        const { data: response } = await queryFulfilled;
        const { product, isRemoveAction = false } = data;

        if (response?.success) {
          if (isRemoveAction) {
            dispatch(
              productsApi.util.updateQueryData(
                'getProductsByType',
                ProductTypeListParams.ShowFavourites,
                (draft) => {
                  draft = draft.filter((pr) => pr.id !== product.id);
                  return draft;
                }
              )
            );
          } else {
            dispatch(
              productsApi.util.updateQueryData(
                'getProductsByType',
                ProductTypeListParams.ShowFavourites,
                (draft) => {
                  draft = [...draft, product];
                  return draft;
                }
              )
            );
          }
        }
      }
    }),
    toggleFavoriteProductById: builder.mutation({
      queryFn: async (data: { productId: number; isRemoveAction: boolean }) => {
        const { productId, isRemoveAction = false } = data;
        try {
          let response: FavoriteToggleResponse = {};
          if (isRemoveAction) {
            const { data: deleteResponse } =
              await api.deleteProductFromFavorite(productId);
            response = deleteResponse;
          } else {
            const { data: addResponse } =
              await api.addProductToFavorite(productId);
            response = addResponse;
          }

          return { data: response };
        } catch (error) {
          console.error(`Error while add favorite location:: ${error.message}`);

          return { error: serializeQueryError(error) };
        }
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data: response } = await queryFulfilled;

        if (response?.success) {
          const { data } = await api.getProductsByType({
            [ProductTypeListParams.ShowFavourites]: 1
          });

          dispatch(
            productsApi.util.updateQueryData(
              'getProductsByType',
              ProductTypeListParams.ShowFavourites,
              (draft) => {
                draft = data?.items ?? [];
                return draft;
              }
            )
          );
        }
      }
    }),
    getBrands: builder.query<BrandType[], string>({
      queryFn: async () => {
        try {
          const { data } = await api.getBrands();

          return { data: data ?? [] };
        } catch (error) {
          console.error(
            `Error while get products brands list: ${error.message}`
          );

          return { error: serializeQueryError(error) };
        }
      }
    }),
    getCategories: builder.query<CategoryType[], string>({
      queryFn: async () => {
        try {
          const { data } = await api.getCategories();

          return { data: data ?? [] };
        } catch (error) {
          console.error(
            `Error while get products categories list: ${error.message}`
          );

          return { error: serializeQueryError(error) };
        }
      }
    }),
    getProductsByFilter: builder.query<
      { items: ProductItem[]; total: number },
      {
        type: FilterSubType;
        id: number | 0;
        page: number;
        categoriesSlug?: string;
        brandsSlug?: string;
      }
    >({
      async queryFn(
        { type, id, page, categoriesSlug, brandsSlug },
        { dispatch }
      ) {
        try {
          const params: GetProductsByFilterParams & { page: number } = { page };

          if (categoriesSlug) {
            params.categories_slug = categoriesSlug;
          } else if (brandsSlug) {
            params.brands_slug = brandsSlug;
          } else if (type === FilterSubType.BRANDS && id !== null) {
            params.brands = id;
          } else if (type === FilterSubType.CATEGORY && id !== null) {
            params.categories = id;
          }

          const hasSlugFilter = Boolean(categoriesSlug || brandsSlug);
          const hasSpecificCategory =
            type === FilterSubType.CATEGORY && id != null && id !== 0;
          const hasSpecificBrand =
            type === FilterSubType.BRANDS && id != null && id !== 0;
          if (!hasSlugFilter && !hasSpecificCategory && !hasSpecificBrand) {
            params.show_recommended = 1;
          }

          const { data } = await api.getProductsByFilter(params);

          const listCacheId = categoriesSlug || brandsSlug || String(id ?? 0);

          dispatch(
            savePageDataById({
              id: listCacheId,
              page,
              data: data.items
            })
          );

          return {
            data: {
              items: data?.items ?? [],
              total: data?.details?.total_count ?? 0
            }
          };
        } catch (error) {
          console.error(`Error while getting products list: ${error.message}`);
          return { error: serializeQueryError(error) };
        }
      },
      serializeQueryArgs: ({
        queryArgs: { id, type, page, categoriesSlug, brandsSlug }
      }) =>
        `${type}-${id ?? 'null'}-page-${page}-cslug-${
          categoriesSlug ?? ''
        }-bslug-${brandsSlug ?? ''}`,
      providesTags: ['ProductsByFilter']
    }),

    getProductDetails: builder.query<ProductItem, string | number>({
      queryFn: async (id) => {
        try {
          const { data } = await api.getProductById(id);

          return { data };
        } catch (error) {
          console.error(`Error while get product details: ${error.message}`);

          return { error: serializeQueryError(error) };
        }
      },
      providesTags: ['ProductDetail']
    }),
    addItemNotify: builder.mutation<SuccessResponse, number>({
      invalidatesTags: ['ProductDetail'],
      queryFn: async (prid: number) => {
        try {
          const {
            data: { success }
          } = await api.shoppingCartAddNotify(prid);

          return { data: { success } };
        } catch (error) {
          console.error(
            `Error while add product to shop cart: ${error.message}`
          );

          return { error: serializeQueryError(error) };
        }
      },
      async onQueryStarted(productId, { dispatch, queryFulfilled }) {
        try {
          const { data: response } = await queryFulfilled;

          if (response?.success) {
            Object.values(ProductTypeListParams).forEach((productType) => {
              dispatch(
                productsApi.util.updateQueryData(
                  'getProductsByType',
                  productType,
                  (draft) => {
                    const productIndex = draft.findIndex(
                      (product) => product.id === productId
                    );
                    if (productIndex !== -1) {
                      draft[productIndex] = {
                        ...draft[productIndex],
                        in_stock_notify: 1
                      };
                    }
                    return draft;
                  }
                )
              );
            });

            dispatch(
              productsApi.util.updateQueryData(
                'getPurchasedProducts',
                undefined,
                (draft) => {
                  const productIndex = draft.findIndex(
                    (product) => product.id === productId
                  );
                  if (productIndex !== -1) {
                    draft[productIndex] = {
                      ...draft[productIndex],
                      in_stock_notify: 1
                    };
                  }
                  return draft;
                }
              )
            );

            dispatch(
              productsApi.util.updateQueryData(
                'getProductDetails',
                productId,
                (draft) => {
                  if (draft) {
                    draft.in_stock_notify = 1;
                  }
                  return draft;
                }
              )
            );

            dispatch(productsApi.util.invalidateTags(['ProductsByFilter']));
            dispatch(updateProductInStockNotify({ productId, notifyValue: 1 }));
          }
        } catch (error) {
          console.error('Error in addItemNotify onQueryStarted:', error);
        }
      }
    }),
    removeItemNotify: builder.mutation<SuccessResponse, number>({
      invalidatesTags: ['ProductDetail'],
      queryFn: async (prid: number) => {
        try {
          const {
            data: { success }
          } = await api.shoppingCartRemoveNotify(prid);
          return { data: { success } };
        } catch (error) {
          console.error(
            `Error while remove product from shop cart: ${error.message}`
          );
          return { error: serializeQueryError(error) };
        }
      },
      async onQueryStarted(productId, { dispatch, queryFulfilled }) {
        try {
          const { data: response } = await queryFulfilled;

          if (response?.success) {
            Object.values(ProductTypeListParams).forEach((productType) => {
              dispatch(
                productsApi.util.updateQueryData(
                  'getProductsByType',
                  productType,
                  (draft) => {
                    const productIndex = draft.findIndex(
                      (product) => product.id === productId
                    );
                    if (productIndex !== -1) {
                      draft[productIndex] = {
                        ...draft[productIndex],
                        in_stock_notify: 0
                      };
                    }
                    return draft;
                  }
                )
              );
            });

            dispatch(
              productsApi.util.updateQueryData(
                'getPurchasedProducts',
                undefined,
                (draft) => {
                  const productIndex = draft.findIndex(
                    (product) => product.id === productId
                  );
                  if (productIndex !== -1) {
                    draft[productIndex] = {
                      ...draft[productIndex],
                      in_stock_notify: 0
                    };
                  }
                  return draft;
                }
              )
            );

            dispatch(
              productsApi.util.updateQueryData(
                'getProductDetails',
                productId,
                (draft) => {
                  if (draft) {
                    draft.in_stock_notify = 0;
                  }
                  return draft;
                }
              )
            );

            dispatch(productsApi.util.invalidateTags(['ProductsByFilter']));
            dispatch(updateProductInStockNotify({ productId, notifyValue: 0 }));
          }
        } catch (error) {
          console.error('Error in removeItemNotify onQueryStarted:', error);
        }
      }
    })
  })
});

export const {
  useGetProductsByTypeQuery,
  useToggleFavoriteProductMutation,
  useToggleFavoriteProductByIdMutation,
  useGetBrandsQuery,
  useGetCategoriesQuery,
  useGetProductsByFilterQuery,
  useGetProductDetailsQuery,
  useGetPurchasedProductsQuery,
  useAddItemNotifyMutation,
  useRemoveItemNotifyMutation
} = productsApi;
